import React, { createContext, useState, useContext, ReactNode } from "react";

import { useDisclosure } from "@chakra-ui/react";
import { Module, Exercise, Course } from "utils/types";

// Define the shape of the context data
interface DataContextType {
  currentModule: Module | null;
  setCurrentModule: React.Dispatch<any>;
  currentCourse: Course | null;
  setCurrentCourse: React.Dispatch<any>;
  modules: Module[];
  setModules: React.Dispatch<any>;
  currentExercises: Exercise[];
  setCurrentExercises: React.Dispatch<any>;
  isSettingsOpen: boolean;
  onSettingsOpen: () => void;
  onSettingsClose: () => void;
  courses: Course[];
  isCoursesLoading: boolean;
  myCourses: Course[];
}

// Create a context with a default value of undefined
const DataContext = createContext<DataContextType | undefined>(undefined);

// Define props for the DataProvider component
interface DataProviderProps {
  children: ReactNode;
}

// The DataProvider component
export const DataProvider: React.FC<DataProviderProps> = ({ children }) => {
  const [modules, setModules] = useState<Module[]>([]);
  const [currentCourse, setCurrentCourse] = useState<Course | null>(null);
  const [currentModule, setCurrentModule] = useState<Module | null>(null);
  const [currentExercises, setCurrentExercises] = useState<Exercise[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [myCourses, setMyCourses] = useState<Course[]>([]);
  const [isCoursesLoading, setIsCoursesLoading] = useState<boolean>(true);

  const {
    isOpen: isSettingsOpen,
    onOpen: onSettingsOpen,
    onClose: onSettingsClose,
  } = useDisclosure();

  // Return a valid ReactNode
  return (
    <DataContext.Provider
      value={{
        currentModule,
        setCurrentModule,
        currentCourse,
        setCurrentCourse,
        modules,
        setModules,
        currentExercises,
        setCurrentExercises,
        isSettingsOpen,
        onSettingsOpen,
        onSettingsClose,
        courses,
        isCoursesLoading,
        myCourses,
      }}
    >
      {children}
    </DataContext.Provider>
  );
};

// Custom hook to use the DataContext
export const useData = (): DataContextType => {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error("useData must be used within a DataProvider");
  }
  return context;
};
